export const REVISION_DETAIL_FIELD_SECTIONS = Object.freeze([
  {
    key: "identity",
    title: "Identificación",
    fields: [
      { name: "codigoBeneficiario", label: "Código de beneficiario", maxLength: 55, nullable: true },
      { name: "cedula", label: "Cédula", maxLength: 45, nullable: true },
      { name: "nombre", label: "Nombre", maxLength: 45, nullable: true },
      { name: "apellido", label: "Apellido", maxLength: 45, nullable: true },
      { name: "nombreRepresentante", label: "Nombre del representante", maxLength: 45, nullable: true },
      { name: "apellidoRepresentante", label: "Apellido del representante", maxLength: 255 },
    ],
  },
  {
    key: "contact",
    title: "Contacto",
    fields: [{ name: "telefono", label: "Teléfono", maxLength: 45, nullable: true }],
  },
  {
    key: "demographics",
    title: "Datos demográficos",
    fields: [
      { name: "sexo", label: "Sexo", maxLength: 45, nullable: true },
      { name: "genero", label: "Género", maxLength: 255 },
      { name: "edad", label: "Edad", maxLength: 255 },
      { name: "fechaNacimiento", label: "Fecha de nacimiento", type: "date", nullable: true },
      { name: "rangoEdad", label: "Rango de edad", maxLength: 45, nullable: true },
      { name: "lgbtqi", label: "LGBTIQ+", maxLength: 45 },
      { name: "ingresos", label: "Ingresos", maxLength: 45, nullable: true },
      { name: "cabezaFamilia", label: "Cabeza de familia", maxLength: 45 },
    ],
  },
  {
    key: "location",
    title: "Ubicación",
    fields: [
      { name: "estado", label: "Estado", maxLength: 45, nullable: true, protected: true },
      { name: "municipio", label: "Municipio", maxLength: 45, nullable: true, protected: true },
      { name: "parroquia", label: "Parroquia", maxLength: 45, nullable: true, protected: true },
      { name: "comunidad", label: "Comunidad", maxLength: 45, nullable: true },
      { name: "centro", label: "Centro", maxLength: 255, protected: true },
    ],
  },
  {
    key: "evaluation",
    title: "Evaluación",
    fields: [
      { name: "fechaEvaluacion", label: "Fecha de evaluación", type: "date", nullable: true, protected: true },
      { name: "peso", label: "Peso", maxLength: 45 },
    ],
  },
  {
    key: "clinical",
    title: "Información clínica",
    fields: [
      { name: "comentarios", label: "Comentarios", maxLength: 45, nullable: true, multiline: true },
    ],
  },
]);

const cloneJson = (value) => JSON.parse(JSON.stringify(value ?? null));

const civilDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value);
};

export const orderedTextsToSlots = (items = []) => {
  const slots = Array(5).fill("");
  (Array.isArray(items) ? items : []).forEach((item) => {
    const order = Number(item?.order ?? item?.orden);
    if (order >= 1 && order <= 5) {
      slots[order - 1] = item?.text ?? item?.texto ?? "";
    }
  });
  return slots;
};

export const slotsToOrderedTexts = (slots = []) =>
  Array.from({ length: 5 }, (_, index) => ({
    order: index + 1,
    text: String(slots[index] ?? "").trim(),
  })).filter((item) => item.text !== "");

export const createRevisionDetailDraft = (detail) => {
  const fields = cloneJson(detail?.fields || {}) || {};

  ["fechaNacimiento", "fechaEvaluacion"].forEach((fieldName) => {
    const group = fieldName === "fechaNacimiento" ? "demographics" : "evaluation";
    if (fields[group]) fields[group][fieldName] = civilDate(fields[group][fieldName]);
  });

  return {
    fields,
    diagnoses: orderedTextsToSlots(detail?.diagnoses),
    consultationReasons: orderedTextsToSlots(detail?.fields?.evaluation?.consultationReasons),
  };
};

const sameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const normalizeRevisionFieldInput = (definition, value) => {
  if (value === "" && (definition.nullable || definition.type === "date")) return null;
  return value;
};

export const buildRevisionDetailChanges = (serverDetail, draft) => {
  if (!serverDetail || !draft) return {};
  const baseline = createRevisionDetailDraft(serverDetail);
  const changes = {};

  REVISION_DETAIL_FIELD_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.protected) return;
      const before = baseline.fields?.[section.key]?.[field.name] ?? null;
      const after = draft.fields?.[section.key]?.[field.name] ?? null;
      if (!sameValue(before, after)) {
        if (!changes[section.key]) changes[section.key] = {};
        changes[section.key][field.name] = after;
      }
    });
  });

  if (!sameValue(baseline.consultationReasons, draft.consultationReasons)) {
    changes.evaluation = {
      ...(changes.evaluation || {}),
      consultationReasons: slotsToOrderedTexts(draft.consultationReasons),
    };
  }

  if (!sameValue(baseline.diagnoses, draft.diagnoses)) {
    changes.diagnoses = slotsToOrderedTexts(draft.diagnoses);
  }

  return changes;
};

export const hasRevisionDetailChanges = (changes) => Object.keys(changes || {}).length > 0;

export const validateRevisionDetailDraft = (draft) => {
  const errors = {};
  REVISION_DETAIL_FIELD_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => {
      const value = draft?.fields?.[section.key]?.[field.name];
      if (field.maxLength && typeof value === "string" && value.length > field.maxLength) {
        errors[`${section.key}.${field.name}`] = `Máximo ${field.maxLength} caracteres`;
      }
    });
  });

  ["diagnoses", "consultationReasons"].forEach((name) => {
    (draft?.[name] || []).forEach((value, index) => {
      if (String(value || "").length > 255) errors[`${name}.${index}`] = "Máximo 255 caracteres";
    });
  });
  return errors;
};

export const validateMarkErrorReason = (value) => {
  const reason = String(value || "").trim();
  if (reason.length < 3) return "Escribe un motivo de al menos 3 caracteres";
  if (reason.length > 500) return "El motivo no puede superar 500 caracteres";
  if (/[\u0000-\u001F\u007F<>]/.test(reason)) return "El motivo contiene caracteres no permitidos";
  return "";
};

export const getRevisionDetailError = (error) => {
  const status = Number(error?.response?.status || 0);
  const code = error?.response?.data?.error?.code || "";
  if (status === 404 || code === "RESOURCE_NOT_FOUND") {
    return { status, code, message: "No tienes acceso a este registro o ya no está disponible." };
  }
  if (status === 409 && code === "VERSION_CONFLICT") {
    return { status, code, message: "Este registro fue actualizado por otro usuario." };
  }
  if (status === 409 && code === "INVALID_STATE") {
    return { status, code, message: "El estado del registro cambió y esta acción ya no está disponible." };
  }
  if (status === 400 || code === "INVALID_REQUEST") {
    return { status, code, message: "Revisa los datos ingresados e intenta nuevamente." };
  }
  if (status === 401) {
    return { status, code, message: "Tu sesión no es válida o ha expirado." };
  }
  return { status, code, message: "No fue posible cargar o actualizar el registro." };
};

export const getRevisionDiagnosisText = (diagnosis) =>
  diagnosis?.text || diagnosis?.diagnostico || diagnosis?.concepto || diagnosis?.textoOriginal || "Sin descripcion";

export const isRevisionDetailContextValid = (detail, convocatoriaId) =>
  Boolean(detail) && String(detail.convocatoriaId) === String(convocatoriaId);
